import { useCallback, useRef,useState } from 'react';

import { apiClient } from '@config/server/apiClient';
import { authEndpoints } from '@config/server/apiEndpoints';

interface DocsTokenResponse {
	url: string;
}

const PUBLIC_DOCS_URL = 'https://docs.ekhodealer.com';
const REFETCH_COOLDOWN_MS = 5000; // Don't refetch within 5 seconds

// Default redirect path for fallback (public docs)
const DEFAULT_REDIRECT = '/buyer-docs/introduction/what-is-ekho';

/**
 * Hook for handling documentation authentication
 * Prefetches JWT on hover with 5-second cooldown to avoid API spam
 * Uses the new /auth/docs-token endpoint which reads from custom claims
 */
export const useDocsAuth = () => {
	const [prefetchedUrl, setPrefetchedUrl] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Track fetch state to avoid duplicate requests and implement cooldown
	const fetchInProgress = useRef(false);
	const lastFetchTime = useRef<number>(0);
	const prefetchFailed = useRef(false);

	/**
	 * Fetch a fresh docs token from the API
	 */
	const fetchToken = useCallback(
		async (redirectPath?: string): Promise<string | null> => {
			try {
				const response = await apiClient.post<DocsTokenResponse>(
					authEndpoints.docsToken(),
					{ redirect: redirectPath || undefined },
				);

				if (response.success && response.data?.url) {
					return response.data.url;
				}
				return null;
			} catch {
				// Silently fail - JWT auth is optional, will fall back to public docs
				return null;
			}
		},
		[],
	);

	/**
	 * Prefetch token on hover (with 5-second cooldown)
	 */
	const prefetchToken = useCallback(async () => {
		// Skip if fetch already in progress
		if (fetchInProgress.current) {
			return;
		}

		// Skip if within cooldown period
		const now = Date.now();
		if (now - lastFetchTime.current < REFETCH_COOLDOWN_MS) {
			return;
		}

		fetchInProgress.current = true;

		try {
			const url = await fetchToken();

			if (url) {
				setPrefetchedUrl(url);
				lastFetchTime.current = Date.now();
			} else {
				// This is expected when JWT auth isn't configured (e.g., still on password auth)
				prefetchFailed.current = true;
				lastFetchTime.current = Date.now();
			}
		} finally {
			fetchInProgress.current = false;
		}
	}, [fetchToken]);

	/**
	 * Get the authenticated docs URL and open it in a new tab
	 * @param redirectPath - Optional path to redirect to within the docs
	 */
	const openDocs = useCallback(
		async (redirectPath?: string) => {
			setError(null);

			// Use prefetched URL if available
			if (prefetchedUrl) {
				const finalUrl = redirectPath
					? prefetchedUrl.replace(
							/redirect=[^&#]*/,
							`redirect=${encodeURIComponent(redirectPath)}`,
						)
					: prefetchedUrl;

				window.open(finalUrl, '_blank');

				// Clear prefetched URL and allow new prefetch
				setPrefetchedUrl(null);
				lastFetchTime.current = 0;
				return;
			}

			// No prefetched URL - fetch fresh
			setIsLoading(true);
			try {
				const url = await fetchToken(redirectPath);

				if (url) {
					window.open(url, '_blank');
					lastFetchTime.current = Date.now();
				} else {
					// JWT auth not available, fall back to public docs
					const fallbackRedirect = redirectPath || DEFAULT_REDIRECT;
					const publicUrl = `${PUBLIC_DOCS_URL}${fallbackRedirect}`;
					window.open(publicUrl, '_blank');
				}
			} catch {
				// Fallback to public docs if authentication fails
				const fallbackRedirect = redirectPath || DEFAULT_REDIRECT;
				const publicUrl = `${PUBLIC_DOCS_URL}${fallbackRedirect}`;
				window.open(publicUrl, '_blank');
			} finally {
				setIsLoading(false);
			}
		},
		[prefetchedUrl, fetchToken],
	);

	return {
		openDocs,
		prefetchToken,
		isLoading,
		error,
		isReady: !!prefetchedUrl,
	};
};
